// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import IconArrowRight from "@conklin/XDL/Icons/IconArrowRight";
import { buttonTransparent } from "@conklin/XDL/PreStyle/button";
import { fontConfig } from "@conklin/XDL/PreStyle/typography";
import color from "@conklin/XDL/Utilities/color";
import { mergeWithCss } from "@conklin/XDL/Utilities/mergeWithCss";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { IconWrapper } from "@insite/mobius/Icon";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { getProp } from "@insite/mobius/utilities";
import * as React from "react";
import { css } from "styled-components";

export const linkArrowStyle = (variant?: "small" | "large", linkStyles?: LinkPresentationProps, iconSize?: number) =>
    ({
        ...mergeWithCss(
            {
                icon: {
                    iconProps: {
                        src: IconArrowRight,
                        size: iconSize || (variant === "large" ? 14 : 11),
                        color: "common.ui9",
                        css: css`
                            opacity: 0.5;
                            margin-left: 8px;
                            // margin-bottom: ${variant === "large" ? 0 : "2px"};

                            &,
                            & svg {
                                height: auto;
                            }
                        `,
                    },
                    position: "right",
                },
                typographyProps: {
                    size: variant === "large" ? 16 : 12,
                    ellipsis: true,
                    weight: fontConfig.weight.bold,
                    css: css`
                        letter-spacing: 0;
                        line-height: ${variant === "large" ? "19px" : "15px"};
                    `,
                },
                css: css`
                    justify-content: left;
                    align-self: flex-start;

                    &:hover {
                        & > ${TypographyStyle}, & > ${IconWrapper} {
                            color: ${color("common.ui10")};
                        }
                    }
                `,
            },
            linkStyles,
        ),
    } as LinkPresentationProps);

interface IconButtonProps {
    typographyProps?: TypographyProps;
}

export const iconButtonCommonStyles = (
    iconSrc: React.ComponentType | string,
    iconSize?: number,
    buttonProps?: IconButtonProps,
    iconColor?: string,
    buttonStyles?: ButtonPresentationProps,
) =>
    ({
        ...mergeWithCss(
            {
                mergeCss: true,
                icon: {
                    src: iconSrc,
                    position: "left",
                },
                typographyProps: {
                    color: "common.ui9",
                    size: 12,
                    weight: fontConfig.weight.bold,
                    ...buttonProps?.typographyProps,
                    css: css`
                        letter-spacing: 0;
                        line-height: 15px;
                        &:hover {
                            color: ${color("common.ui10")};
                        }
                        ${buttonProps?.typographyProps?.css}
                    `,
                },
                css: css`
                    padding: 0;
                    ${buttonTransparent}
                    ${IconWrapper} {
                        margin-right: 6px;
                        opacity: 0.5;

                        &,
                        & svg {
                            width: ${iconSize || 11}px;
                            height: auto;
                            color: ${color(iconColor || "common.ui9")};
                        }
                    }

                    &:hover:not([disabled]),
                    &:focus:not([disabled]),
                    &:focus:hover {
                        &,
                        & ${IconWrapper}, ${TypographyStyle} {
                            color: ${color("common.ui10")};
                        }
                    }

                    &:disabled {
                        & ${TypographyStyle}, & ${IconWrapper} {
                            color: ${color("common.ui9")};
                            background-color: transparent;
                        }
                    }
                `,
            },
            buttonStyles,
        ),
    } as ButtonPresentationProps);

export const iconLinkCommonStyles = (iconSrc: React.ComponentType | string) => {
    return {
        color: "text.link",
        icon: {
            iconProps: {
                src: iconSrc,
                size: 13,
                color: "text.link",
                css: css`
                    width: 12px;
                    margin-right: 8px;
                    opacity: 0.5;
                `,
            },
            position: "left",
        },
        typographyProps: {
            size: 12,
            weight: fontConfig.weight.bold,
            css: css`
                letter-spacing: 0;
                line-height: 17px;
            `,
        },
        css: css`
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
            &:disabled {
                opacity: 0.44;
            }
            &:not(:disabled):hover {
                &,
                & > ${TypographyStyle}, & ${IconWrapper} {
                    color: ${color("common.ui3")};
                }
                & ${IconWrapper} {
                    opacity: 1;
                }
            }
        `,
    } as LinkPresentationProps;
};
